import { useState, type ReactNode } from "react";
import { AlertTriangle, Bot, CheckCircle2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useLocale, useTranslation, legalBasisText } from "@ncp/i18n";
import { Badge, StatusBadge, Tooltip, TooltipProvider, cn } from "@ncp/ui";
import type { Band, ChecklistItem, ProvisionAssessment, ProvisionFlag, ReviewStatus, Severity, Verdict } from "./api";

export const BAND_ORDER: Band[] = ["None", "Low", "Moderate", "High", "Severe"];
export const BAND_COLOR: Record<Band, string> = { None: "#6b665d", Low: "#2f6b3a", Moderate: "#86692a", High: "#9a5f14", Severe: "#b23a3a" };
const BAND_VARIANT: Record<Band, "default" | "success" | "gold" | "warning" | "danger"> = { None: "default", Low: "success", Moderate: "gold", High: "warning", Severe: "danger" };
const SEVERITY_VARIANT: Record<Severity, "default" | "info" | "warning" | "danger"> = { None: "default", Indicative: "info", Likely: "warning", Strong: "danger" };
const SEVERITY_CELL: Record<Severity, string> = { None: "bg-white text-sand-500", Indicative: "bg-seablue-50 text-seablue-800", Likely: "bg-warning-50 text-warning-700", Strong: "bg-danger-50 text-danger-700" };
const VERDICT_VARIANT: Record<Verdict, "success" | "gold" | "warning" | "danger"> = { NoConcern: "success", MinorConcerns: "gold", SignificantConcerns: "warning", SevereConcerns: "danger" };
const STATUS_MAP: Record<ReviewStatus, string> = { Received: "Draft", Screening: "Submitted", Analysing: "UnderReview", InReview: "AwaitingApplicant", Adopted: "Published" };

export function bandFor(score: number): Band { return score <= 0 ? "None" : score < 15 ? "Low" : score < 35 ? "Moderate" : score < 60 ? "High" : "Severe"; }

export function BandBadge({ band, score, size }: { band: Band; score?: number; size?: "sm" | "default" }) {
  const { t } = useTranslation();
  return <Badge variant={BAND_VARIANT[band]} size={size} dot>{score !== undefined ? `${score} · ` : ""}{t(`backoffice:advocacy.band.${band}`)}</Badge>;
}

export function SeverityBadge({ severity, size = "sm" }: { severity: Severity; size?: "sm" | "default" }) {
  const { t } = useTranslation();
  return <Badge variant={SEVERITY_VARIANT[severity] ?? "default"} size={size}>{t(`backoffice:advocacy.severity.${severity}`, { defaultValue: severity })}</Badge>;
}

export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  const { t } = useTranslation();
  const Icon = verdict === "NoConcern" ? CheckCircle2 : AlertTriangle;
  return <Badge variant={VERDICT_VARIANT[verdict] ?? "default"}><Icon /> {t(`backoffice:advocacy.verdict.${verdict}`, { defaultValue: verdict })}</Badge>;
}

export function ReviewStatusBadge({ status }: { status: ReviewStatus }) {
  const { t } = useTranslation();
  const live = status === "Screening" || status === "Analysing" || status === "Received";
  return <StatusBadge status={STATUS_MAP[status] ?? status} label={t(`backoffice:advocacy.status.${status}`, { defaultValue: status })} pulse={live} />;
}

/** The restrictiveness index as a banded bar with the score – the same bands the PDF and the rules engine use. */
export function RestrictivenessGauge({ score, band, compact = false, className }: { score: number; band: Band; compact?: boolean; className?: string }) {
  const { t } = useTranslation();
  const s = Math.max(0, Math.min(100, score));
  const stops: Array<[Band, number]> = [["Low", 15], ["Moderate", 20], ["High", 25], ["Severe", 40]];
  return (
    <div className={className}>
      <div className="flex items-baseline gap-2">
        <span className={cn("font-heading font-semibold tabular", compact ? "text-2xl" : "text-4xl")} style={{ color: BAND_COLOR[band] }}>{s}</span>
        <span className="text-sm text-muted-foreground">/100</span>
        <span className="ms-auto"><BandBadge band={band} /></span>
      </div>
      <div className="relative mt-2 h-2.5 w-full overflow-hidden rounded-full bg-sand-100">
        <div className="absolute inset-y-0 start-0 rounded-full transition-all duration-700" style={{ width: `${Math.max(s, 1)}%`, background: BAND_COLOR[band] }} />
      </div>
      {!compact ? (
        <div className="mt-1 flex text-[11px] text-muted-foreground">
          {stops.map(([b, w]) => (
            <span key={b} style={{ width: `${w}%` }} className="truncate border-s border-sand-300 ps-1">{t(`backoffice:advocacy.band.${b}`)}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface ChecklistCell { code: string; provisions: number; worstSeverity: Severity; reading?: string }

/** OECD A1–D3 + CMA E1–E2 as a grid of tiles grouped by category, coloured by the worst severity found. */
export function ChecklistMatrix({ items, cells, onSelect, selected }: { items: ChecklistItem[]; cells: ChecklistCell[]; onSelect?: (code: string | null) => void; selected?: string | null }) {
  const { locale } = useLocale();
  const byCode = new Map(cells.map((c) => [c.code, c]));
  const groups = new Map<string, ChecklistItem[]>();
  for (const it of items) groups.set(it.category, [...(groups.get(it.category) ?? []), it]);
  return (
    <TooltipProvider>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[...groups.entries()].map(([cat, its]) => (
          <div key={cat} className="rounded-xl border border-sand-200 bg-white p-3">
            <p className="mb-2 line-clamp-2 text-[11px] font-semibold uppercase tracking-wider text-sand-600" title={locale === "ar" ? its[0]?.categoryAr : its[0]?.categoryEn}>{locale === "ar" ? its[0]?.categoryAr : its[0]?.categoryEn}</p>
            <div className="flex flex-wrap gap-1.5">
              {its.map((it) => {
                const c = byCode.get(it.code);
                const sev: Severity = c && c.provisions > 0 ? c.worstSeverity : "None";
                const active = selected === it.code;
                return (
                  <Tooltip key={it.code} content={<span className="block max-w-xs text-xs" dir="auto">{it.code} · {locale === "ar" ? it.questionAr : it.questionEn}{c?.reading ? <><br /><span className="opacity-80">{c.reading}</span></> : null}</span>}>
                    <button type="button" onClick={() => onSelect?.(active ? null : it.code)} className={cn("flex h-9 min-w-11 items-center justify-center gap-1 rounded-lg border px-2 text-xs font-semibold transition", SEVERITY_CELL[sev], active ? "border-aeblack-900 ring-2 ring-aegold-300" : "border-sand-200", c && c.provisions > 0 ? "hover:-translate-y-0.5 hover:shadow-sm" : "opacity-70")}>
                      {it.code}{c && c.provisions > 0 ? <span className="rounded-full bg-white/80 px-1 text-[10px] tabular">{c.provisions}</span> : null}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}

export interface ProvisionRowView {
  locator: string; index?: number; quote: string; codes: string[]; severity: Severity; restriction: string; alternative: string; screenVerdict?: string;
  mechanism?: string; whoIsAffected?: string; objective?: string; legalBasis?: string; matches?: string[]; notes?: string[]; mitigated?: boolean; fromScreen: boolean;
}

export function rowsFromAssessment(provisions: ProvisionAssessment[]): ProvisionRowView[] {
  return provisions.map((p) => ({ locator: p.locator, quote: p.quote, codes: p.checklistCodes, severity: p.severity, restriction: p.restriction, alternative: p.alternative, screenVerdict: p.screenVerdict, mechanism: p.mechanism, whoIsAffected: p.whoIsAffected, objective: p.objective, legalBasis: p.legalBasis, fromScreen: false }));
}

const RANK: Record<Severity, number> = { None: 0, Indicative: 1, Likely: 2, Strong: 3 };

export function rowsFromScreen(flags: ProvisionFlag[], alternatives: (code: string) => string): ProvisionRowView[] {
  const groups = new Map<number, ProvisionFlag[]>();
  for (const f of flags) groups.set(f.provisionIndex, [...(groups.get(f.provisionIndex) ?? []), f]);
  return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([index, fs]) => {
    const worst = fs.reduce((a, b) => (RANK[b.severity] > RANK[a.severity] ? b : a));
    const codes = [...new Set(fs.map((f) => f.code))].sort();
    return {
      index, locator: worst.locator, quote: worst.excerpt, codes, severity: worst.severity, restriction: [...new Set(fs.flatMap((f) => f.notes))].join("; "),
      alternative: [...new Set(codes.map(alternatives))].join(" / "), matches: [...new Set(fs.flatMap((f) => f.matches))], mitigated: fs.some((f) => f.mitigated), fromScreen: true,
    };
  });
}

/** Provision-by-provision findings; each row expands to the mechanism, who pays, objective, alternative and legal basis. */
export function ProvisionsTable({ rows, filterCode }: { rows: ProvisionRowView[]; filterCode?: string | null }) {
  const { t } = useTranslation();
  const [openRow, setOpenRow] = useState<string | null>(null);
  const visible = filterCode ? rows.filter((r) => r.codes.includes(filterCode)) : rows;
  if (visible.length === 0) return <p className="rounded-xl border border-dashed border-sand-300 p-6 text-center text-sm text-muted-foreground">{t("backoffice:advocacy.detail.noFlags")}</p>;
  return (
    <div className="overflow-hidden rounded-xl border border-sand-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-sand-50 text-xs text-sand-600">
          <tr>
            <th className="px-3 py-2 text-start">{t("backoffice:advocacy.detail.provisionColumns.provision")}</th>
            <th className="px-3 py-2 text-start">{t("backoffice:advocacy.detail.provisionColumns.restriction")}</th>
            <th className="px-3 py-2 text-start">{t("backoffice:advocacy.detail.provisionColumns.items")}</th>
            <th className="px-3 py-2 text-start">{t("backoffice:advocacy.detail.provisionColumns.severity")}</th>
            <th className="hidden px-3 py-2 text-start lg:table-cell">{t("backoffice:advocacy.detail.provisionColumns.alternative")}</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => {
            const key = `${r.locator}-${r.index ?? ""}`;
            const open = openRow === key;
            return (
              <Row key={key} r={r} open={open} onToggle={() => setOpenRow(open ? null : key)} />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Row({ r, open, onToggle }: { r: ProvisionRowView; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const detail: Array<[string, string | undefined]> = [
    [t("backoffice:advocacy.detail.provisionFields.mechanism"), r.mechanism],
    [t("backoffice:advocacy.detail.provisionFields.who"), r.whoIsAffected],
    [t("backoffice:advocacy.detail.provisionFields.objective"), r.objective],
    [t("backoffice:advocacy.detail.provisionFields.alternative"), r.alternative],
    [t("backoffice:advocacy.detail.provisionFields.legalBasis"), legalBasisText(r.legalBasis, locale) ?? undefined],
  ];
  return (
    <>
      <tr className={cn("cursor-pointer border-t border-sand-100 align-top transition hover:bg-sand-50/60", open && "bg-sand-50/60")} onClick={onToggle}>
        <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-aeblack-950" dir="auto">{r.locator}</td>
        <td className="px-3 py-2.5" dir="auto">
          <p className="line-clamp-2">{r.restriction || r.quote}</p>
          {r.fromScreen && r.matches?.length ? <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{t("backoffice:advocacy.detail.matched")}: {r.matches.slice(0, 4).join(" · ")}</p> : null}
        </td>
        <td className="whitespace-nowrap px-3 py-2.5 text-xs font-semibold">{r.codes.join(", ")}</td>
        <td className="px-3 py-2.5"><SeverityBadge severity={r.severity} />{r.mitigated ? <span className="ms-1 text-[11px] text-muted-foreground" title={t("backoffice:advocacy.detail.mitigated")}>↓</span> : null}</td>
        <td className="hidden px-3 py-2.5 text-xs text-muted-foreground lg:table-cell" dir="auto"><p className="line-clamp-2">{r.alternative}</p></td>
        <td className="px-2 py-2.5 text-sand-500">{open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}</td>
      </tr>
      {open ? (
        <tr className="border-t border-sand-100 bg-sand-50/40">
          <td colSpan={6} className="px-4 py-3">
            {r.quote ? <blockquote className="mb-3 border-s-2 border-aegold-400 ps-3 text-sm italic leading-relaxed text-aeblack-900/80" dir="auto">“{r.quote}”</blockquote> : null}
            {r.fromScreen ? (
              <p className="text-xs text-muted-foreground">{t("backoffice:advocacy.detail.screenOnly")}</p>
            ) : (
              <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {detail.filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><dt className="text-[11px] uppercase tracking-wider text-sand-600">{k}</dt><dd className="mt-0.5 text-sm leading-relaxed" dir="auto">{v}</dd></div>
                ))}
                {r.screenVerdict ? <div><dt className="text-[11px] uppercase tracking-wider text-sand-600">{t("backoffice:advocacy.detail.provisionFields.screen")}</dt><dd className="mt-0.5 text-sm">{r.screenVerdict}</dd></div> : null}
              </dl>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function SectionTitle({ children, hint, action, icon: Icon }: { children: ReactNode; hint?: ReactNode; action?: ReactNode; icon?: typeof Sparkles }) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-aeblack-950">{Icon ? <Icon className="size-4 text-aegold-700" /> : null}{children}</h3>
        {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function AiDraftNote({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-3 rounded-2xl border border-seablue-200 bg-seablue-50/40 p-4 text-sm"><Bot className="size-5 shrink-0 text-seablue-700" /> <span>{children}</span></div>;
}
